/**
 * useParentHomeState — nav + assessment modal (create/edit/analyze/delete) + gestes d'une séance.
 *
 * Les quatre actions rapides viennent d'ici : trois sont des gestes (`useParentSessionGesture`,
 * partagé avec « Tous mes lives »), la quatrième ouvre le formulaire legacy d'heures de soutien.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { Linking } from "react-native"
import { useDispatch, useSelector } from "react-redux"
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native"

import { formIds } from "../../../../../../data/constants/formConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { chaptersService } from "../../../../../../data/services/chaptersService"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { type MediaPickerProps } from "../../../../../../design-system/organisms/MediaPicker"
import {
    applySchoolAssessmentPreview,
    canSubmitCreateSchoolAssessment,
    draftFromUISchoolAssessment,
    emptyCreateSchoolAssessment,
} from "../../../formatters/createSchoolAssessmentFormatter"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { useParentSessionGesture } from "../../../hooks/useParentSessionGesture"
import { useSchoolAssessmentAnalyzeAdapter } from "../../../hooks/useSchoolAssessmentAnalyzeAdapter"
import {
    type UICreateSchoolAssessment,
    type UIParentTodo,
    type UILiveSession,
    type UIParentTutorChoice,
    type UIRevisionProgram,
    type UISchoolAssessment,
    type UISectionedChapter,
} from "../../../types"
import { useLiveSessionsListState } from "../LiveSessions/useLiveSessionsListState"
import { PARENT_TAB_BAR_HEIGHT } from "../ParentTabBar"

type PickedImage = { uri: string; width?: number; height?: number }

type Feedback = { title: string; message: string }

type DeleteConfirm = {
    assessmentId: string | number
    title: string
    message: string
}

type UiState = {
    feedback: Feedback | null
    deleteConfirm: DeleteConfirm | null
}

/**
 * Les quatre questions de l'accueil passent par UNE boîte, discriminée par son `kind` — sans quoi
 * l'écran monterait quatre `<ConfirmDialog>` dont trois invisibles (modèle :
 * `useSubscriptionPortalState`). L'ordre ci-dessous EST la priorité d'affichage, celle que donnait
 * l'empilement du JSX : un accusé passe devant la question qui vient de le produire.
 */
export type ParentHomeConfirmKind = "feedback" | "delete-assessment" | "cancel-flag" | "manage-rdv"

export type ParentHomeConfirm = {
    kind: ParentHomeConfirmKind
    title: string
    message?: string
    confirmLabel: string
    cancelLabel: string
    danger: boolean
}

type UiAction =
    | { type: "OPEN_FEEDBACK"; feedback: Feedback }
    | { type: "CLOSE_FEEDBACK" }
    | { type: "OPEN_DELETE"; confirm: DeleteConfirm }
    | { type: "CLOSE_DELETE" }

const initialUi: UiState = { feedback: null, deleteConfirm: null }

function uiReducer(state: UiState, action: UiAction): UiState {
    switch (action.type) {
        case "OPEN_FEEDBACK":
            return { ...state, feedback: action.feedback }
        case "CLOSE_FEEDBACK":
            return { ...state, feedback: null }
        case "OPEN_DELETE":
            return { ...state, deleteConfirm: action.confirm }
        case "CLOSE_DELETE":
            return { ...state, deleteConfirm: null }
        default:
            return state
    }
}

export type UseParentHomeStateArgs = {
    studentEmail?: string | null
    studentId?: string | number | null
    identityReady?: boolean
    /** Le hash du dernier bilan — ce qui ouvre la route `r`. Absent : la carte n'est pas là. */
    lastReportHash?: string | null
    /** Prénom de l'enfant — ce que les gestes disent au parent. */
    childFirstname?: string
    /** Les prochaines séances, triées : l'étape « Quelle séance ? » des gestes. */
    nextSessions?: UILiveSession[]
    /**
     * Les séances de cet enfant ne sont pas encore arrivées (`vm.loading`) : un geste ouvert d'ailleurs
     * (« décalez la séance ») attend qu'elles soient là pour passer ses gardes (séance suspendue, contact).
     */
    sessionsLoading?: boolean
    /** Les tuteurs de l'élève : l'étape « Quel tuteur ? », sautée quand il n'y en a qu'un. */
    tutors?: UIParentTutorChoice[]
    assessments?: UISchoolAssessment[]
    /** Catalog chapters from the VM — merged with AI preview chapters for the form. */
    catalogChapters?: UISectionedChapter[]
    /** True when the shell has a blocking pricing/identity error (legacy → leave parent mode). */
    hasShellError?: boolean
    /** La déconnexion de la coquille — le bouton tourne jusqu'à son issue. */
    startLogout?: () => void
    onRefreshPricing?: () => void
    onAssessmentsChanged?: () => void
    /** Rejoué après un drapeau d'absence ou de retard — la liste doit montrer le nouvel état. */
    onLivesChanged?: () => void
    /**
     * Quitte l'espace parent pour celui de l'enfant, sur cette séance (legacy
     * `handleExitToChild` + intention de navigation). ABSENT = pas de ligne « Rejoindre ».
     */
    onJoinAsChild?: (session: UILiveSession) => void
    /**
     * Ouvre le formulaire legacy « heures de soutien » en 3 étapes, dont la visibilité est tenue
     * par l'enveloppe legacy qui monte cet écran (`ParentHomeWithExtraHours`) — `ports/**` n'a pas
     * le droit d'importer d'UI legacy. La garde d'abonnement, elle, arrive en
     * `addOneOffLessonDisabled` : la tuile reste à sa place dans la rangée, grisée.
     */
    onAddOneOffLesson?: () => void
    /**
     * Le lien de régularisation de la famille (`currentUser.payment_url`) : celui que porte la
     * ligne « À faire », et celui que propose le dialogue d'une séance suspendue. Un seul lien,
     * une seule porte — les deux passent par `openExternalUrl`.
     */
    paymentUrl?: string | null
    /**
     * Ouvre « Planifier un rattrapage » en feuille, sur l'accueil (`useRecoverAbsenceFlow`) — la
     * ligne « À faire » du rattrapage n'a pas de route. Le legacy poussait l'écran `recover-absence`.
     */
    onPlanRecovery?: () => void
    /** Ouvre « Garder son tuteur » en feuille sur ce choix (`useStayWithTutorFlow`). */
    onChooseTutorChange?: (moveId: string | number) => void
}

export function useParentHomeState(args: UseParentHomeStateArgs) {
    const {
        studentEmail = null,
        studentId = null,
        identityReady = false,
        lastReportHash = null,
        childFirstname,
        nextSessions = [],
        sessionsLoading = false,
        tutors = [],
        assessments = [],
        catalogChapters = [],
        hasShellError = false,
        startLogout,
        onRefreshPricing,
        onAssessmentsChanged,
        onAddOneOffLesson,
        onLivesChanged,
        onJoinAsChild,
        paymentUrl = null,
        onPlanRecovery,
        onChooseTutorChange,
    } = args
    const dispatch = useDispatch()
    const navigation = useNavigation<{
        push: (name: string, params?: Record<string, unknown>) => void
        navigate: (name: string, params?: Record<string, unknown>) => void
        setParams: (params: Record<string, unknown>) => void
    }>()
    const route = useRoute<{ key: string; name: string; params?: { triggerMoveSession?: boolean } }>()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cameraOn = useSelector((state: any) => !!state?.activity?.cameraOn)
    const liveMenu = useLiveSessionsListState({ studentEmail })
    /**
     * Le NAVIGATEUR du système pour le lien de reprise : son funnel porte l'identité dans son URL
     * signée. Le lien de régularisation, lui, passe par `openExternalUrl` (voir `onTodoPress`).
     */
    const openInBrowser = useCallback((url: string) => {
        void Linking.openURL(url)
    }, [])
    const gesture = useParentSessionGesture({
        sessions: nextSessions,
        surface: "parent_home",
        tutors,
        childFirstname,
        studentEmail,
        onOpenTutorMessage: liveMenu.actions.openMessageForTutor,
        onCancelFlag: liveMenu.actions.onLiveMenuAction,
        onJoinAsChild,
        paymentUrl,
        refresh: onLivesChanged,
        // La barre d'onglets est sous l'accueil : le bandeau d'accusé se pose au-dessus d'elle.
        snackbarBottomOffset: PARENT_TAB_BAR_HEIGHT,
    })
    const [ui, dispatchUi] = useReducer(uiReducer, initialUi)

    /**
     * L'intention posée par « Modifier mes créneaux » (« décalez la séance ») : le geste s'ouvre ici,
     * là où vivent les séances qu'il propose. Tant que les séances de l'enfant ne sont pas arrivées,
     * l'intention attend (le paramètre reste) : le geste lancé sur une liste vide sauterait la séance
     * suspendue et le contact. Le paramètre se vide une fois lu, sinon un retour sur l'onglet
     * rouvrirait la feuille.
     */
    const startMoveSession = gesture.onMoveSession
    useFocusEffect(
        useCallback(() => {
            if (!route.params?.triggerMoveSession || sessionsLoading) return
            startMoveSession()
            navigation.setParams({ triggerMoveSession: undefined })
        }, [route.params?.triggerMoveSession, sessionsLoading, startMoveSession, navigation])
    )

    const [assessmentModalVisible, setAssessmentModalVisible] = useState(false)
    const [editingAssessmentId, setEditingAssessmentId] = useState<string | number | null>(null)
    const [images, setImages] = useState<PickedImage[]>([])
    const [pdf, setPdf] = useState<string | null>(null)
    const [createAssessmentData, setCreateAssessmentData] =
        useState<UICreateSchoolAssessment>(emptyCreateSchoolAssessment())

    const lastChaptersSubjectRef = useRef<string | number | null>(null)

    const isEditing = editingAssessmentId != null

    const mediaProps = useMediaPickerAdapter({
        images,
        setImages,
        pdf,
        setPdf,
        multiple: true,
        enableExternalLink: false,
        buttonLabel: "Photo ou document",
        formId: formIds.NEW_SCHOOL_ASSESSMENT,
    }) as MediaPickerProps

    const { onAnalyze, analyzeLoading } = useSchoolAssessmentAnalyzeAdapter({
        studentId,
        images,
        pdf,
        onSuccess: (preview) => {
            setCreateAssessmentData((prev) => applySchoolAssessmentPreview(prev, preview))
        },
        onError: (message) => {
            dispatchUi({ type: "OPEN_FEEDBACK", feedback: { title: "Erreur", message } })
        },
    })

    // Fetch catalog chapters when the create draft subject changes.
    useEffect(() => {
        const subjectId = createAssessmentData.subjectId
        if (subjectId == null || !identityReady) return
        if (lastChaptersSubjectRef.current != null && String(lastChaptersSubjectRef.current) === String(subjectId)) {
            return
        }
        lastChaptersSubjectRef.current = subjectId
        dispatch(
            apiRequest(chaptersService.getChaptersBySchoolSubjectId, { schoolSubjectId: subjectId }, {}, {}) as never
        )
    }, [dispatch, createAssessmentData.subjectId, identityReady])

    const resetAssessmentDraft = useCallback(() => {
        setImages([])
        setPdf(null)
        setCreateAssessmentData(emptyCreateSchoolAssessment())
        setEditingAssessmentId(null)
        lastChaptersSubjectRef.current = null
    }, [])

    const closeAssessmentModal = useCallback(() => {
        setAssessmentModalVisible(false)
        resetAssessmentDraft()
    }, [resetAssessmentDraft])

    const openAddAssessment = useCallback(() => {
        resetAssessmentDraft()
        setAssessmentModalVisible(true)
    }, [resetAssessmentDraft])

    const onEditAssessment = useCallback(
        (id: UISchoolAssessment["id"]) => {
            const assessment = assessments.find((a) => String(a.id) === String(id))
            if (assessment == null) return
            setEditingAssessmentId(assessment.id)
            setCreateAssessmentData(draftFromUISchoolAssessment(assessment))
            setImages([])
            setPdf(null)
            lastChaptersSubjectRef.current = null
            setAssessmentModalVisible(true)
        },
        [assessments]
    )

    const onSeeMoreLives = useCallback(() => {
        if (studentEmail) {
            navigation.push("live-sessions-list", { student_email: studentEmail })
            return
        }
        navigation.push("live-sessions-list")
    }, [navigation, studentEmail])

    const onOpenRevisionProgram = useCallback(
        (program: UIRevisionProgram) => {
            if (program.isStageProgram) {
                navigation.push("stage-program")
                return
            }
            navigation.push("study-program", { id: program.id })
        },
        [navigation]
    )

    /**
     * Une tâche « À faire » mène là où son bandeau legacy menait : un écran de l'app, ou le
     * NAVIGATEUR pour la reprise self-serve — son funnel porte l'identité dans son lien signé, une
     * navigation in-app le perdrait.
     *
     * « Régulariser » ouvre le lien de régularisation comme le changement de moyen de paiement,
     * par `openExternalUrl` (navigateur intégré, onglet sur le web) : ce lien est sur le domaine de
     * l'app, et `Linking.openURL` le rendait à l'app elle-même (liens universels) — le parent
     * atterrissait sur un autre accueil au lieu de la page de paiement.
     */
    const onTodoPress = useCallback(
        (todo: UIParentTodo) => {
            if (todo.kind === "payment") {
                openExternalUrl(todo.externalUrl)
                return
            }
            if (todo.externalUrl) {
                openInBrowser(todo.externalUrl)
                return
            }
            if (todo.kind === "makeup") {
                onPlanRecovery?.()
                return
            }
            if (todo.kind === "tutor_change") {
                if (todo.moveId != null) onChooseTutorChange?.(todo.moveId)
                return
            }
            // La seule tâche qui porte encore une route va sur un ONGLET (« Activer la suite »), où
            // le legacy `navigate` : un `push` y empilerait un bouton « retour » qu'il n'a pas.
            if (todo.route) navigation.navigate(todo.route, todo.routeParams)
        },
        [navigation, openInBrowser, onPlanRecovery, onChooseTutorChange]
    )

    const onOpenLastReport = useCallback(() => {
        if (!lastReportHash) return
        navigation.push("r", { h: lastReportHash })
    }, [navigation, lastReportHash])

    const onDeleteAssessment = useCallback((id: UISchoolAssessment["id"]) => {
        dispatchUi({
            type: "OPEN_DELETE",
            confirm: {
                assessmentId: id,
                title: "Supprimer ce contrôle ?",
                message: "Cette action est irréversible.",
            },
        })
    }, [])

    const { start: startDelete } = useApiMutation({
        service: schoolAssessmentsService.deleteSchoolAssessment,
        fallback: "Impossible de supprimer le contrôle.",
        // Plusieurs lignes peuvent viser cette tranche : `id` dit laquelle est la nôtre.
        scopeParam: "id",
        onSuccess: () => onAssessmentsChanged?.(),
        onError: (message) => dispatchUi({ type: "OPEN_FEEDBACK", feedback: { title: "Erreur", message } }),
    })

    const confirmDeleteAssessment = useCallback(() => {
        const id = ui.deleteConfirm?.assessmentId
        dispatchUi({ type: "CLOSE_DELETE" })
        if (id == null) return
        startDelete({ id })
    }, [startDelete, ui.deleteConfirm?.assessmentId])

    const buildPayload = useCallback(() => {
        return {
            student_id: studentId,
            school_subject_id: createAssessmentData.subjectId,
            date: createAssessmentData.date,
            chapter_ids: createAssessmentData.chapterIds,
            custom_chapters:
                createAssessmentData.customChapters.length > 0
                    ? createAssessmentData.customChapters.map((c) => c.label)
                    : undefined,
            key_requirements: createAssessmentData.keyRequirementsText.trim() || undefined,
        }
    }, [studentId, createAssessmentData])

    const finishSaveSuccess = useCallback(
        (title: string, message: string) => {
            setAssessmentModalVisible(false)
            resetAssessmentDraft()
            onAssessmentsChanged?.()
            dispatchUi({ type: "OPEN_FEEDBACK", feedback: { title, message } })
        },
        [onAssessmentsChanged, resetAssessmentDraft]
    )

    /** Un seul envoi : la présence d'un id à modifier décide du service, et des mots de l'accusé. */
    const { start: startSave, requesting: saving } = useApiMutation({
        service: isEditing
            ? schoolAssessmentsService.updateSchoolAssessment
            : schoolAssessmentsService.createSchoolAssessment,
        fallback: isEditing ? "Impossible de modifier le contrôle." : "Impossible d'ajouter le contrôle.",
        idleOnUnmount: [
            schoolAssessmentsService.createSchoolAssessment,
            schoolAssessmentsService.updateSchoolAssessment,
        ],
        onSuccess: () =>
            finishSaveSuccess(
                isEditing ? "Contrôle modifié" : "Contrôle ajouté",
                isEditing ? "Le contrôle a bien été mis à jour." : "Le contrôle a bien été enregistré."
            ),
        onError: (message) => dispatchUi({ type: "OPEN_FEEDBACK", feedback: { title: "Erreur", message } }),
    })

    const onSubmitAssessment = useCallback(() => {
        if (studentId == null || !canSubmitCreateSchoolAssessment(createAssessmentData)) return
        startSave(editingAssessmentId != null ? { id: editingAssessmentId } : {}, buildPayload())
    }, [startSave, studentId, createAssessmentData, editingAssessmentId, buildPayload])

    // Merge AI-detected chapters into the catalog so Autocomplete can show selected AI ids.
    const chapters = useMemo(() => {
        const byId = new Map(catalogChapters.map((c) => [String(c.id), c]))
        for (const c of createAssessmentData.aiPreview?.chapters ?? []) {
            byId.set(String(c.id), c)
        }
        return [...byId.values()]
    }, [catalogChapters, createAssessmentData.aiPreview?.chapters])

    const closeFeedback = useCallback(() => {
        // Deux sources d'accusé se partagent la boîte : celui de l'accueil, celui du menu d'une séance.
        if (ui.feedback) dispatchUi({ type: "CLOSE_FEEDBACK" })
        else liveMenu.actions.closeFeedback()
    }, [ui.feedback, liveMenu.actions])

    const onRetry = useCallback(() => {
        if (hasShellError && startLogout) {
            startLogout()
            return
        }
        onRefreshPricing?.()
        onAssessmentsChanged?.()
    }, [hasShellError, startLogout, onRefreshPricing, onAssessmentsChanged])

    const feedback = ui.feedback ?? liveMenu.feedback
    const confirm = useMemo((): ParentHomeConfirm | null => {
        if (feedback) {
            return {
                kind: "feedback",
                ...feedback,
                confirmLabel: "OK",
                cancelLabel: "Fermer",
                danger: false,
            }
        }
        if (ui.deleteConfirm) {
            return {
                kind: "delete-assessment",
                title: ui.deleteConfirm.title,
                message: ui.deleteConfirm.message,
                confirmLabel: "Supprimer",
                cancelLabel: "Annuler",
                danger: true,
            }
        }
        if (liveMenu.cancelConfirm) {
            return {
                kind: "cancel-flag",
                title: liveMenu.cancelConfirm.title,
                message: liveMenu.cancelConfirm.message,
                confirmLabel: "Oui, annuler",
                cancelLabel: "Non",
                danger: true,
            }
        }
        if (liveMenu.manageRdv) {
            return {
                kind: "manage-rdv",
                title: liveMenu.manageRdv.title,
                message: liveMenu.manageRdv.message,
                confirmLabel: liveMenu.manageRdv.confirmLabel,
                cancelLabel: liveMenu.manageRdv.cancelLabel,
                danger: false,
            }
        }
        return null
    }, [feedback, ui.deleteConfirm, liveMenu.cancelConfirm, liveMenu.manageRdv])

    /** Ce que chaque question fait de ses deux boutons ; le fond suit l'annulation, sauf « RDV ». */
    const dialogHandlers: Record<
        ParentHomeConfirmKind,
        { onConfirm: () => void; onCancel: () => void; onDismiss?: () => void }
    > = useMemo(
        () => ({
            feedback: { onConfirm: closeFeedback, onCancel: closeFeedback },
            "delete-assessment": {
                onConfirm: confirmDeleteAssessment,
                onCancel: () => dispatchUi({ type: "CLOSE_DELETE" }),
            },
            "cancel-flag": { onConfirm: liveMenu.actions.confirmCancel, onCancel: liveMenu.actions.closeCancel },
            // « Email » est une ACTION du menu RDV, pas une sortie : le fond a son propre geste.
            "manage-rdv": {
                onConfirm: liveMenu.actions.confirmManageRdv,
                onCancel: liveMenu.actions.cancelManageRdv,
                onDismiss: liveMenu.actions.dismissManageRdv,
            },
        }),
        [closeFeedback, confirmDeleteAssessment, liveMenu.actions]
    )

    const actions = useMemo(
        () => ({
            onSessionAction: gesture.onSessionAction,
            onBlockedSessionPress: gesture.onBlockedSessionPress,
            onMoveSession: gesture.onMoveSession,
            onReportLate: gesture.onReportLate,
            onMessageTutor: gesture.onMessageTutor,
            onSeeMoreLives,
            onTodoPress,
            onOpenLastReport,
            onOpenRevisionProgram,
            onDeleteAssessment,
            onEditAssessment,
            onOpenAssessmentModal: openAddAssessment,
            onCloseAssessmentModal: closeAssessmentModal,
            onAssessmentDetails: () => {
                // Accordion already expands; dedicated details modal deferred.
            },
            onAddOneOffLesson,
            onCreateAssessmentDataChange: setCreateAssessmentData,
            onSubmitAssessment,
            onAnalyzeAssessment: onAnalyze,
            onRetry,
            onConfirmDialog: () => confirm && dialogHandlers[confirm.kind].onConfirm(),
            onCancelDialog: () => confirm && dialogHandlers[confirm.kind].onCancel(),
            onDismissDialog: () => {
                if (!confirm) return
                const handlers = dialogHandlers[confirm.kind]
                ;(handlers.onDismiss ?? handlers.onCancel)()
            },
        }),
        [
            gesture.onSessionAction,
            gesture.onBlockedSessionPress,
            gesture.onMoveSession,
            gesture.onReportLate,
            gesture.onMessageTutor,
            confirm,
            dialogHandlers,
            onAddOneOffLesson,
            onSeeMoreLives,
            onTodoPress,
            onOpenLastReport,
            onOpenRevisionProgram,
            onDeleteAssessment,
            onEditAssessment,
            openAddAssessment,
            closeAssessmentModal,
            onSubmitAssessment,
            onAnalyze,
            onRetry,
        ]
    )

    return {
        actions,
        gestures: gesture.gestures,
        joinAsChildFirstname: gesture.joinAsChildFirstname,
        assessmentModalVisible,
        isEditingAssessment: isEditing,
        assessmentModalTitle: isEditing ? "Modifier le contrôle" : "Ajouter un contrôle",
        createAssessmentData,
        chapters,
        mediaProps,
        hideAssessmentModalForCamera: cameraOn,
        analyzeAssessmentLoading: analyzeLoading,
        submitAssessmentDisabled: !canSubmitCreateSchoolAssessment(createAssessmentData),
        submitAssessmentLoading: saving,
        confirm,
        messageModal: liveMenu.messageModal,
    }
}
